import type { User } from '@gmn-portal/backend';
import { useEffect, useMemo, useState } from 'react';
import {
  type EntityPermissionOverviewEntry,
  useEntityPermissionOverview,
  useUserGroups,
  useUsers,
  type Where,
} from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import { PaginationNavigation } from '@/shared/components/pagination';
import { TableSearchBar } from '@/shared/components/tables';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import type { AclServiceKey } from '@/shared/utils/permissions';
import { UsersAccessTable } from './users-access-table';

type ViewAsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: AclServiceKey;
  entityId: string;
  customerId?: string | null;
  selectedUserId?: string | null;
  onSelectUser: (user: User) => void;
};

type UserListSection = {
  id: 'gmn' | 'customer';
  title: string;
  users: User[];
  totalPages: number;
  totalItems: number;
  page: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
};

export function ViewAsDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  customerId,
  selectedUserId,
  onSelectUser,
}: ViewAsDialogProps) {
  const { t } = useIntl();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [gmnPage, setGmnPage] = useState(1);
  const [gmnRequestedPage, setGmnRequestedPage] = useState(1);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerRequestedPage, setCustomerRequestedPage] = useState(1);
  const pageSize = 5;

  const { data: userGroupsData } = useUserGroups({
    limit: 0,
    sort: 'key',
    disabled: !open,
  });
  const userGroups = userGroupsData?.docs ?? [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group])),
    [userGroups]
  );

  const gmnGroupIds = useMemo(
    () => userGroups.filter((group) => group.domain === 'gmn').map((group) => group.id),
    [userGroups]
  );

  const customerGroupIds = useMemo(
    () => userGroups.filter((group) => group.domain === 'customer').map((group) => group.id),
    [userGroups]
  );

  const searchFilter = useMemo<Where | null>(() => {
    const value = debouncedSearch.trim();
    if (!value) return null;
    const conditions: Where[] = [
      { email: { like: value } },
      { firstName: { like: value } },
      { lastName: { like: value } },
    ];
    return {
      or: conditions,
    };
  }, [debouncedSearch]);

  const gmnWhere = useMemo(() => {
    if (gmnGroupIds.length === 0) return undefined;
    const and: Where[] = [{ userGroup: { in: gmnGroupIds } }, { customer: { exists: false } }];
    if (searchFilter) {
      and.push(searchFilter);
    }
    return { and };
  }, [gmnGroupIds, searchFilter]);

  const customerWhere = useMemo(() => {
    if (!customerId || customerGroupIds.length === 0) return undefined;
    const and: Where[] = [
      { userGroup: { in: customerGroupIds } },
      { customer: { equals: customerId } },
    ];
    if (searchFilter) {
      and.push(searchFilter);
    }
    return { and };
  }, [customerGroupIds, customerId, searchFilter]);

  const {
    data: gmnUsersData,
    isLoading: isLoadingGmnUsers,
    isFetching: isFetchingGmnUsers,
  } = useUsers({
    depth: 0,
    page: gmnRequestedPage,
    limit: pageSize,
    sort: 'lastName',
    where: gmnWhere,
    disabled: !open || !gmnWhere,
  });

  const {
    data: customerUsersData,
    isLoading: isLoadingCustomerUsers,
    isFetching: isFetchingCustomerUsers,
  } = useUsers({
    depth: 0,
    page: customerRequestedPage,
    limit: pageSize,
    sort: 'lastName',
    where: customerWhere,
    disabled: !open || !customerWhere,
  });

  useEffect(() => {
    setGmnRequestedPage(1);
    setCustomerRequestedPage(1);
  }, [debouncedSearch, customerId]);

  useEffect(() => {
    if (!gmnUsersData || isFetchingGmnUsers) return;
    const nextPage = gmnUsersData.page ?? gmnRequestedPage;
    if (nextPage !== gmnPage) {
      setGmnPage(nextPage);
    }
  }, [gmnPage, gmnRequestedPage, gmnUsersData, isFetchingGmnUsers]);

  useEffect(() => {
    if (!customerUsersData || isFetchingCustomerUsers) return;
    const nextPage = customerUsersData.page ?? customerRequestedPage;
    if (nextPage !== customerPage) {
      setCustomerPage(nextPage);
    }
  }, [customerPage, customerRequestedPage, customerUsersData, isFetchingCustomerUsers]);

  const displayGmnUsers = (gmnUsersData?.docs ?? []) as User[];
  const displayCustomerUsers = (customerUsersData?.docs ?? []) as User[];

  const overviewUserIds = useMemo(() => {
    const ids = [...displayGmnUsers, ...displayCustomerUsers].map((user) => user.id);
    return Array.from(new Set(ids));
  }, [displayCustomerUsers, displayGmnUsers]);

  const {
    data: overviewData,
    isLoading: isLoadingOverview,
    isFetching: isFetchingOverview,
  } = useEntityPermissionOverview({
    entityType,
    entityId,
    userIds: overviewUserIds,
    disabled: !open || overviewUserIds.length === 0,
  });

  const overviewByUserId = useMemo(() => {
    const map = new Map<string, EntityPermissionOverviewEntry>();
    for (const entry of overviewData?.entries ?? []) {
      map.set(entry.userId, entry);
    }
    return map;
  }, [overviewData?.entries]);

  const hasDisplayUsers = displayGmnUsers.length + displayCustomerUsers.length > 0;
  const isAccessLoading =
    isLoadingOverview ||
    isFetchingOverview ||
    (!hasDisplayUsers &&
      (isLoadingGmnUsers ||
        isLoadingCustomerUsers ||
        isFetchingGmnUsers ||
        isFetchingCustomerUsers));

  const renderTable = (section: UserListSection) => {
    return (
      <div className="space-y-4">
        <UsersAccessTable
          users={section.users}
          userGroupsById={userGroupsById}
          overviewByUserId={overviewByUserId}
          isOverviewLoading={isAccessLoading}
          renderAction={({ user, readAllowed }) => {
            const isSelected = selectedUserId === user.id;
            return isSelected ? (
              <Badge variant="outline-solid">{t('permissions.viewAs.active')}</Badge>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid={`view-as-select-${user.id}`}
                onClick={() => {
                  onSelectUser(user);
                }}
                disabled={readAllowed !== true}
              >
                {t('permissions.viewAs.select')}
              </Button>
            );
          }}
        />
        {section.totalPages > 1 ? (
          <PaginationNavigation
            currentPage={section.page}
            totalPages={section.totalPages}
            pageSize={pageSize}
            totalItems={section.totalItems}
            onPageChange={section.onPageChange}
            isLoading={section.isLoading}
          />
        ) : null}
      </div>
    );
  };

  const sections: UserListSection[] = [
    {
      id: 'gmn',
      title: t('permissions.gmnUsers'),
      users: displayGmnUsers,
      totalPages: gmnUsersData?.totalPages ?? 0,
      totalItems: gmnUsersData?.totalDocs ?? 0,
      page: gmnPage,
      onPageChange: setGmnRequestedPage,
      isLoading: isLoadingGmnUsers || isFetchingGmnUsers,
    },
    {
      id: 'customer',
      title: t('permissions.customerUsers'),
      users: displayCustomerUsers,
      totalPages: customerUsersData?.totalPages ?? 0,
      totalItems: customerUsersData?.totalDocs ?? 0,
      page: customerPage,
      onPageChange: setCustomerRequestedPage,
      isLoading: isLoadingCustomerUsers || isFetchingCustomerUsers,
    },
  ];

  const visibleSections = customerId
    ? sections
    : sections.filter((section) => section.id === 'gmn');

  const hasAnyUsers = (gmnUsersData?.totalDocs ?? 0) + (customerUsersData?.totalDocs ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] !max-w-[90rem]">
        <DialogHeader>
          <DialogTitle>{t('permissions.viewAs.title')}</DialogTitle>
          <DialogDescription>{t('permissions.viewAs.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-col md:flex-row gap-3">
            <TableSearchBar
              searchTerm={search}
              onSearchChange={setSearch}
              placeholder={t('permissions.viewAs.searchPlaceholder')}
            />
          </div>

          {visibleSections.map((section) => (
            <div key={section.id} className="space-y-2">
              <div className="text-sm font-semibold text-foreground">{section.title}</div>
              {renderTable(section)}
            </div>
          ))}

          {!isAccessLoading && !hasAnyUsers ? (
            <div className="text-sm text-muted-foreground">{t('permissions.viewAs.noUsers')}</div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
